import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, getDocs, doc, updateDoc, query, where, getDoc } from 'firebase/firestore';
import {
  Box, Container, Typography, Button, Paper, TextField,
  IconButton, Alert, CircularProgress, Chip, Divider, Tooltip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PreviewIcon from '@mui/icons-material/Preview';
import UndoIcon from '@mui/icons-material/Undo';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { canEditProfile } from '../utils/permissions';
import RoleSelect from '../components/RoleSelect';
import AIProfileEvaluator from '../components/AIProfileEvaluator';

const SECTIONS = [
  { id: 'ingress', label: 'Ingress', icon: '📝' },
  { id: 'assignments', label: 'Uppdrag', icon: '💼' },
  { id: 'skills', label: 'Kompetenser', icon: '🏆' },
  { id: 'ai', label: 'AI-utvärdering', icon: '🤖' },
  { id: 'status', label: 'Status', icon: '✅' },
];

function EditableField({ fieldKey, editedFields, children, onReset, onSaveToBase }) {
  const isEdited = editedFields?.[fieldKey];
  return (
    <Box sx={{ position: 'relative', mt: isEdited ? 2 : 0 }}>
      {isEdited && (
        <Box sx={{
          position: 'absolute', top: -20, right: 0, zIndex: 10,
          display: 'flex', alignItems: 'center', gap: 0.5
        }}>
          <Chip label="Editerad" size="small"
            sx={{ bgcolor: '#FF6B00', color: 'white',
              fontSize: '0.6rem', height: 18, fontWeight: 'bold' }} />
          {onSaveToBase && (
            <Tooltip title="Spara till bas-CV">
              <IconButton size="small" onClick={onSaveToBase}
                sx={{ bgcolor: '#e8f5e9', width: 22, height: 22,
                  border: '1px solid #a5d6a7' }}>
                <SaveIcon sx={{ fontSize: 12, color: '#2e7d32' }} />
              </IconButton>
            </Tooltip>
          )}
          {onReset && (
            <Tooltip title="Återställ till original">
              <IconButton size="small" onClick={onReset}
                sx={{ bgcolor: '#fff3e0', width: 22, height: 22,
                  border: '1px solid #ffcc80' }}>
                <UndoIcon sx={{ fontSize: 12, color: '#FF6B00' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
      <Box sx={{
        border: isEdited ? '2px solid #FF6B00' : undefined,
        borderRadius: isEdited ? 1 : undefined,
        bgcolor: isEdited ? '#fff8f0' : undefined,
        transition: 'all 0.2s'
      }}>
        {children}
      </Box>
    </Box>
  );
}

export default function TenderVariant() {
  const navigate = useNavigate();
  const { tenderId, userId } = useParams();
  const { currentUser, userProfile } = useAuth();
  // Ändringar får bara föras tillbaka till bas-CV:t om man får redigera profilen
  const canSaveToBase = canEditProfile(userProfile, currentUser?.uid, userId);
  const [variant, setVariant] = useState(null);
  const [variantId, setVariantId] = useState(null);
  const [departmentId, setDepartmentId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');
  const [activeSection, setActiveSection] = useState('ingress');

  useEffect(() => { loadVariant(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadVariant() {
    setLoading(true);
    const snap = await getDocs(
      query(collection(db, 'cvVariants'),
        where('tenderId', '==', tenderId),
        where('userId', '==', userId))
    );
    if (!snap.empty) {
      setVariantId(snap.docs[0].id);
      const data = snap.docs[0].data();
      if (!data.editedFields) data.editedFields = {};
      setVariant(data);
    }
    const tenderSnap = await getDoc(doc(db, 'tenders', tenderId));
    if (tenderSnap.exists()) {
      setDepartmentId(tenderSnap.data().departmentId || '');
    }
    setLoading(false);
  }

  async function handleSave() {
    if (!variantId) return;
    setSaving(true);
    await updateDoc(doc(db, 'cvVariants', variantId), {
      ...variant,
      updatedAt: new Date()
    });
    setSaving(false);
    setSaved('CV-varianten sparad!');
    setTimeout(() => setSaved(''), 3000);
  }

  async function saveFieldToBaseProfile(field, value, assignmentIndex = null) {
    if (!canSaveToBase) return;
    const editStamp = { lastEditedBy: currentUser.uid, lastEditedAt: new Date() };
    try {
      const profileRef = doc(db, 'profiles', userId);
      const profileSnap = await getDoc(profileRef);
      if (!profileSnap.exists()) return;
      if (assignmentIndex !== null) {
        const assignments = [...(profileSnap.data().assignments || [])];
        const baseName = variant.adjustedAssignments[assignmentIndex]?.name;
        const baseIndex = assignments.findIndex(a => a.name === baseName);
        if (baseIndex !== -1) {
          assignments[baseIndex] = { ...assignments[baseIndex], [field]: value };
          await updateDoc(profileRef, { assignments, updatedAt: new Date(), ...editStamp });
          setSaved('Uppdaterat i bas-CV!');
          setTimeout(() => setSaved(''), 3000);
        }
      } else {
        await updateDoc(profileRef, { [field]: value, updatedAt: new Date(), ...editStamp });
        setSaved('Uppdaterat i bas-CV!');
        setTimeout(() => setSaved(''), 3000);
      }
    } catch (err) {
      console.error(err);
    }
  }

  function updateField(field, value) {
    setVariant(prev => ({
      ...prev,
      [field]: value,
      editedFields: { ...prev.editedFields, [field]: true }
    }));
  }

  function resetField(field, originalValue) {
    setVariant(prev => {
      const newEdited = { ...prev.editedFields };
      delete newEdited[field];
      return { ...prev, [field]: originalValue, editedFields: newEdited };
    });
  }

  function updateAssignment(index, field, value) {
    setVariant(prev => {
      const arr = [...prev.adjustedAssignments];
      arr[index] = { ...arr[index], [field]: value };
      const editKey = 'assignment_' + index + '_' + field;
      return {
        ...prev,
        adjustedAssignments: arr,
        editedFields: { ...prev.editedFields, [editKey]: true }
      };
    });
  }

  function resetAssignmentField(index, field) {
    const original = variant.baseSnapshot?.assignments?.find(
      a => a.name === variant.adjustedAssignments[index]?.name
    )?.[field] || '';
    setVariant(prev => {
      const arr = [...prev.adjustedAssignments];
      arr[index] = { ...arr[index], [field]: original };
      const editKey = 'assignment_' + index + '_' + field;
      const newEdited = { ...prev.editedFields };
      delete newEdited[editKey];
      return { ...prev, adjustedAssignments: arr, editedFields: newEdited };
    });
  }

  function removeAssignment(index) {
    setVariant(prev => ({
      ...prev,
      adjustedAssignments: prev.adjustedAssignments.filter((_, i) => i !== index),
      editedFields: { ...prev.editedFields, assignments_list: true }
    }));
  }

  function moveAssignment(index, direction) {
    setVariant(prev => {
      const arr = [...prev.adjustedAssignments];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= arr.length) return prev;
      [arr[index], arr[newIndex]] = [arr[newIndex], arr[index]];
      return {
        ...prev,
        adjustedAssignments: arr,
        editedFields: { ...prev.editedFields, assignments_list: true }
      };
    });
  }

  function addFromBase(assignment) {
    setVariant(prev => ({
      ...prev,
      adjustedAssignments: [...prev.adjustedAssignments, { ...assignment }],
      editedFields: { ...prev.editedFields, assignments_list: true }
    }));
  }

  function addChip(field, value) {
    if (!value.trim()) return;
    setVariant(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), value.trim()],
      editedFields: { ...prev.editedFields, [field]: true }
    }));
  }

  function removeChip(field, index) {
    setVariant(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
      editedFields: { ...prev.editedFields, [field]: true }
    }));
  }

  function handleAIAccept(field, value) {
    if (field === 'tools_expert' || field === 'tools_good' || field === 'tools_basic') {
      const level = field.replace('tools_', '');
      setVariant(prev => ({
        ...prev,
        baseSnapshot: {
          ...prev.baseSnapshot,
          tools: {
            ...prev.baseSnapshot?.tools,
            [level]: [...(prev.baseSnapshot?.tools?.[level] || []), value]
          }
        },
        editedFields: { ...prev.editedFields, ['tools_' + level]: true }
      }));
    } else if (field === 'languages') {
      setVariant(prev => ({
        ...prev,
        baseSnapshot: {
          ...prev.baseSnapshot,
          languages: [...(prev.baseSnapshot?.languages || []),
            { language: value, level: '' }]
        },
        editedFields: { ...prev.editedFields, languages: true }
      }));
    } else if (field === 'competencies') {
      addChip('highlightedSkills', value);
    } else if (field === 'standards') {
      addChip('highlightedStandards', value);
    } else {
      setVariant(prev => ({
        ...prev,
        baseSnapshot: {
          ...prev.baseSnapshot,
          [field]: [...(prev.baseSnapshot?.[field] || []), value]
        },
        editedFields: { ...prev.editedFields, [field]: true }
      }));
    }
  }

  const baseAssignments = variant?.baseSnapshot?.assignments || [];
  const includedNames = variant?.adjustedAssignments?.map(a => a.name) || [];
  const availableFromBase = baseAssignments.filter(a => !includedNames.includes(a.name));
  const editedCount = Object.keys(variant?.editedFields || {}).length;

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress />
    </Box>
  );

  if (!variant) return (
    <Box sx={{ p: 4 }}>
      <Typography>Ingen CV-variant hittades.</Typography>
      <Button onClick={() => navigate('/tenders/' + tenderId + '/team')} sx={{ mt: 2 }}>
        Tillbaka till anbudet
      </Button>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box sx={{ backgroundColor: '#1a1a1a', color: 'white', py: 2, px: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton color="inherit"
              onClick={() => navigate('/tenders/' + tenderId + '/team')}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h5" fontWeight="bold">CV-variant</Typography>
              <Typography variant="caption" sx={{ color: '#aaa' }}>
                {variant.userName}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {editedCount > 0 && (
              <Chip label={editedCount + ' editerade fält'} size="small"
                sx={{ bgcolor: '#FF6B00', color: 'white', fontWeight: 'bold' }} />
            )}
            <Button variant="outlined" startIcon={<PreviewIcon />}
              sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}
              onClick={() => navigate('/tenders/' + tenderId + '/variant/' + userId + '/preview')}>
              Förhandsgranska
            </Button>
            <Button variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSave} disabled={saving}
              sx={{ bgcolor: 'white', color: '#1a1a1a' }}>
              {saving ? 'Sparar...' : 'Spara'}
            </Button>
          </Box>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 3 }}>
        {saved && <Alert severity="success" sx={{ mb: 2 }}>{saved}</Alert>}

        {/* AI-förslag från anbudsanalys */}
        {variant.aiSuggestions?.length > 0 && (
          <Paper sx={{ p: 3, mb: 3, bgcolor: '#fff8e1', border: '1px solid #f9a825' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AutoFixHighIcon sx={{ color: '#f57f17' }} />
              <Typography variant="subtitle1" fontWeight="bold" color="#f57f17">
                AI-förslag på förbättringar
              </Typography>
            </Box>
            {variant.aiSuggestions.map((s, i) => (
              <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>• {s}</Typography>
            ))}
          </Paper>
        )}

        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>

          {/* SIDOMENY */}
          <Paper sx={{ p: 2, minWidth: 200, position: 'sticky', top: 20 }}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold"
              sx={{ px: 1, display: 'block', mb: 1, letterSpacing: 1 }}>
              SEKTIONER
            </Typography>

            {SECTIONS.map(section => {
              const count = section.id === 'assignments'
                ? variant.adjustedAssignments?.length
                : section.id === 'skills'
                ? (variant.highlightedSkills?.length || 0) +
                  (variant.highlightedStandards?.length || 0)
                : null;

              return (
                <Box key={section.id} onClick={() => setActiveSection(section.id)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    px: 1.5, py: 0.8, borderRadius: 1, cursor: 'pointer', mb: 0.2,
                    bgcolor: activeSection === section.id ? '#eeeeee' : 'transparent',
                    borderLeft: activeSection === section.id
                      ? '3px solid #FF6B00' : '3px solid transparent',
                    '&:hover': { bgcolor: '#f0f0f0' }
                  }}>
                  <Typography fontSize={13} sx={{ lineHeight: 1 }}>{section.icon}</Typography>
                  <Typography variant="caption"
                    fontWeight={activeSection === section.id ? 'bold' : 'normal'}
                    sx={{ flex: 1, fontSize: '0.78rem' }}>
                    {section.label}
                  </Typography>
                  {count > 0 && (
                    <Chip label={count} size="small"
                      sx={{ height: 18, fontSize: '0.65rem',
                        bgcolor: activeSection === section.id ? '#FF6B00' : '#e0e0e0',
                        color: activeSection === section.id ? 'white' : '#444'
                      }} />
                  )}
                </Box>
              );
            })}

            <Divider sx={{ my: 1.5 }} />

            <Typography variant="caption" color="text.secondary" fontWeight="bold"
              sx={{ px: 1, display: 'block', mb: 1, letterSpacing: 1 }}>
              INFO
            </Typography>

            <Box sx={{ px: 1 }}>
              <Chip
                label={variant.status === 'draft' ? 'Utkast' :
                  variant.status === 'reviewed' ? 'Granskad' : 'Godkänd'}
                size="small"
                color={variant.status === 'approved' ? 'success' :
                  variant.status === 'reviewed' ? 'warning' : 'default'}
                sx={{ mb: 1 }}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                {variant.baseSnapshot?.assignments?.length || 0} uppdrag i original
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {variant.adjustedAssignments?.length || 0} uppdrag i variant
              </Typography>
              {editedCount > 0 && (
                <Typography variant="caption"
                  sx={{ color: '#FF6B00', display: 'block', mt: 0.5 }}>
                  {editedCount} editerade fält
                </Typography>
              )}
            </Box>
          </Paper>

          {/* INNEHÅLL */}
          <Box sx={{ flex: 1 }}>

            {/* INGRESS */}
            {activeSection === 'ingress' && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  📝 Ingress
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Anpassad av AI för detta anbud – redigera fritt.
                </Typography>
                <EditableField
                  fieldKey="adjustedIngress"
                  editedFields={variant.editedFields}
                  onReset={() => resetField('adjustedIngress',
                    variant.baseSnapshot?.ingress || '')}
                  onSaveToBase={canSaveToBase ? () => saveFieldToBaseProfile(
                    'ingress', variant.adjustedIngress) : undefined}>
                  <TextField multiline rows={6} fullWidth
                    value={variant.adjustedIngress || ''}
                    onChange={e => updateField('adjustedIngress', e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': {
                      bgcolor: variant.editedFields?.adjustedIngress
                        ? '#fff8f0' : undefined
                    }}}
                  />
                </EditableField>
                <Divider sx={{ my: 3 }} />
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Original-ingress (för referens)
                </Typography>
                <Typography variant="body2" sx={{
                  fontStyle: 'italic', color: 'text.secondary',
                  bgcolor: '#f9f9f9', p: 2, borderRadius: 1
                }}>
                  {variant.baseSnapshot?.ingress || 'Ingen ingress i bas-profilen.'}
                </Typography>
                {variant.coverNote && (
                  <Box sx={{ mt: 3, p: 2, bgcolor: '#e8f5e9', borderRadius: 1,
                    border: '1px solid #a5d6a7' }}>
                    <Typography variant="subtitle2" fontWeight="bold"
                      color="#2e7d32" gutterBottom>
                      AI-motivering
                    </Typography>
                    <Typography variant="body2">{variant.coverNote}</Typography>
                  </Box>
                )}
              </Paper>
            )}

            {/* UPPDRAG */}
            {activeSection === 'assignments' && (
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">💼 Utvalda uppdrag</Typography>
                  <Typography variant="caption" color="text.secondary"
                    sx={{ alignSelf: 'center' }}>
                    Använd pilarna för att prioritera ordning
                  </Typography>
                </Box>

                {variant.adjustedAssignments?.map((a, i) => {
                  const baseAssignment = variant.baseSnapshot?.assignments?.find(
                    ba => ba.name === a.name
                  );
                  return (
                    <Paper key={i} variant="outlined" sx={{ p: 2, mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Button size="small" variant="outlined" disabled={i === 0}
                            onClick={() => moveAssignment(i, -1)}
                            sx={{ minWidth: 36, p: 0.5 }}>↑</Button>
                          <Button size="small" variant="outlined"
                            disabled={i === variant.adjustedAssignments.length - 1}
                            onClick={() => moveAssignment(i, 1)}
                            sx={{ minWidth: 36, p: 0.5 }}>↓</Button>
                          <Chip label={'#' + (i + 1)} size="small" />
                        </Box>
                        <IconButton size="small" color="error"
                          onClick={() => removeAssignment(i)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField label="Uppdragsnamn" value={a.name || ''}
                          fullWidth size="small"
                          onChange={e => updateAssignment(i, 'name', e.target.value)} />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <TextField label="Period" value={a.period || ''}
                            fullWidth size="small"
                            onChange={e => updateAssignment(i, 'period', e.target.value)} />
                          <RoleSelect
                            value={a.role || ''}
                            onChange={val => updateAssignment(i, 'role', val)}
                            departmentId={departmentId}
                            size="small"
                          />
                          <TextField label="Uppdragsgivare" value={a.client || ''}
                            fullWidth size="small"
                            onChange={e => updateAssignment(i, 'client', e.target.value)} />
                        </Box>

                        <EditableField
                          fieldKey={'assignment_' + i + '_projectDescription'}
                          editedFields={variant.editedFields}
                          onReset={() => resetAssignmentField(i, 'projectDescription')}
                          onSaveToBase={canSaveToBase ? () => saveFieldToBaseProfile(
                            'projectDescription', a.projectDescription, i) : undefined}>
                          <TextField
                            label="Projektbeskrivning (gemensam)"
                            value={a.projectDescription || ''}
                            fullWidth multiline rows={2} size="small"
                            onChange={e => updateAssignment(i, 'projectDescription',
                              e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': {
                              bgcolor: variant.editedFields?.[
                                'assignment_' + i + '_projectDescription']
                                ? '#fff8f0' : undefined
                            }}}
                          />
                        </EditableField>

                        <EditableField
                          fieldKey={'assignment_' + i + '_personalDescription'}
                          editedFields={variant.editedFields}
                          onReset={() => resetAssignmentField(i, 'personalDescription')}
                          onSaveToBase={canSaveToBase ? () => saveFieldToBaseProfile(
                            'personalDescription', a.personalDescription, i) : undefined}>
                          <TextField
                            label="Min roll och insats"
                            value={a.personalDescription || ''}
                            fullWidth multiline rows={3} size="small"
                            onChange={e => updateAssignment(i, 'personalDescription',
                              e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': {
                              bgcolor: variant.editedFields?.[
                                'assignment_' + i + '_personalDescription']
                                ? '#fff8f0' : undefined
                            }}}
                          />
                        </EditableField>

                        {baseAssignment?.personalDescription && (
                          <Box sx={{ bgcolor: '#f5f5f5', p: 1.5, borderRadius: 1,
                            borderLeft: '3px solid #ddd' }}>
                            <Typography variant="caption" color="text.secondary"
                              fontWeight="bold">
                              Original (referens):
                            </Typography>
                            <Typography variant="caption" color="text.secondary"
                              display="block">
                              {baseAssignment.personalDescription}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  );
                })}

                {availableFromBase.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" fontWeight="bold"
                      color="text.secondary" gutterBottom>
                      Tillgängliga uppdrag från bas-profilen
                    </Typography>
                    {availableFromBase.map((a, i) => (
                      <Box key={i} sx={{
                        display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 1.5, bgcolor: '#f9f9f9', borderRadius: 1, mb: 1
                      }}>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">{a.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {[a.period, a.client, a.role].filter(Boolean).join(' | ')}
                          </Typography>
                        </Box>
                        <Button size="small" startIcon={<AddIcon />}
                          onClick={() => addFromBase(a)}>
                          Lägg till
                        </Button>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            )}

            {/* KOMPETENSER */}
            {activeSection === 'skills' && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  🏆 Kompetenser
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Lyfta kompetenser
                      </Typography>
                      {variant.editedFields?.highlightedSkills && (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Chip label="Editerad" size="small"
                            sx={{ bgcolor: '#FF6B00', color: 'white',
                              fontSize: '0.6rem', height: 18 }} />
                          {canSaveToBase && (
                          <Tooltip title="Spara till bas-CV">
                            <IconButton size="small"
                              onClick={() => saveFieldToBaseProfile(
                                'competencies', variant.highlightedSkills)}
                              sx={{ bgcolor: '#e8f5e9', width: 22, height: 22,
                                border: '1px solid #a5d6a7' }}>
                              <SaveIcon sx={{ fontSize: 12, color: '#2e7d32' }} />
                            </IconButton>
                          </Tooltip>
                          )}
                        </Box>
                      )}
                    </Box>
                    <ChipEditor
                      items={variant.highlightedSkills || []}
                      onAdd={val => addChip('highlightedSkills', val)}
                      onRemove={i => removeChip('highlightedSkills', i)}
                      placeholder="Lägg till kompetens..."
                      color="primary"
                    />
                    {variant.baseSnapshot?.competencies?.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Klicka för att lägga till från bas-profil:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {variant.baseSnapshot.competencies
                            .filter(c => !variant.highlightedSkills?.includes(c))
                            .map((c, i) => (
                              <Chip key={i} label={c} size="small" variant="outlined"
                                sx={{ fontSize: '0.65rem', height: 20, cursor: 'pointer' }}
                                onClick={() => addChip('highlightedSkills', c)}
                              />
                            ))}
                        </Box>
                      </Box>
                    )}
                  </Box>

                  <Divider />

                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Lyfta standarder
                      </Typography>
                      {variant.editedFields?.highlightedStandards && (
                        <Chip label="Editerad" size="small"
                          sx={{ bgcolor: '#FF6B00', color: 'white',
                            fontSize: '0.6rem', height: 18 }} />
                      )}
                    </Box>
                    <ChipEditor
                      items={variant.highlightedStandards || []}
                      onAdd={val => addChip('highlightedStandards', val)}
                      onRemove={i => removeChip('highlightedStandards', i)}
                      placeholder="Lägg till standard..."
                      color="secondary"
                    />
                  </Box>
                </Box>
              </Paper>
            )}

            {/* AI-UTVÄRDERING */}
            {activeSection === 'ai' && (
              <Box>
                <AIProfileEvaluator
                  profile={{
                    ...variant.baseSnapshot,
                    ingress: variant.adjustedIngress,
                    competencies: variant.highlightedSkills,
                    standards: variant.highlightedStandards,
                  }}
                  onAccept={handleAIAccept}
                  tenderContext={variant.coverNote || ''}
                />
              </Box>
            )}

            {/* STATUS */}
            {activeSection === 'status' && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>✅ Status</Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  {[
                    { value: 'draft', label: 'Utkast', desc: 'Under arbete' },
                    { value: 'reviewed', label: 'Granskad', desc: 'Klar för granskning' },
                    { value: 'approved', label: 'Godkänd', desc: 'Klar för anbud' }
                  ].map(s => (
                    <Paper key={s.value} variant="outlined"
                      sx={{
                        p: 2, cursor: 'pointer', flex: 1, textAlign: 'center',
                        bgcolor: variant.status === s.value ? '#eeeeee' : 'background.paper',
                        border: variant.status === s.value
                          ? '2px solid #1a1a1a' : undefined,
                        '&:hover': { bgcolor: '#f0f0f0' }
                      }}
                      onClick={() => updateField('status', s.value)}>
                      <Typography fontWeight="bold">{s.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {s.desc}
                      </Typography>
                    </Paper>
                  ))}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Bas-profil snapshot
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Ändringar i bas-profilen påverkar inte denna variant.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={variant.baseSnapshot?.assignments?.length + ' uppdrag i original'}
                    size="small" />
                  <Chip
                    label={variant.adjustedAssignments?.length + ' uppdrag i variant'}
                    size="small" color="primary" />
                  {editedCount > 0 && (
                    <Chip label={editedCount + ' editerade fält'} size="small"
                      sx={{ bgcolor: '#FF6B00', color: 'white' }} />
                  )}
                </Box>
              </Paper>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

function ChipEditor({ items, onAdd, onRemove, placeholder, color }) {
  const [input, setInput] = useState('');
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField size="small" fullWidth value={input} placeholder={placeholder}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { onAdd(input); setInput(''); } }}
        />
        <Button variant="outlined" onClick={() => { onAdd(input); setInput(''); }}>
          <AddIcon />
        </Button>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {items.map((item, i) => (
          <Chip key={i} label={item} color={color} onDelete={() => onRemove(i)} />
        ))}
      </Box>
    </Box>
  );
}